"use client";
import React from "react";
import { motion } from "framer-motion";
import { Layers3, Search, Zap, Lock, Database, MessageSquare, ArrowRight } from "lucide-react";
import Link from "next/link";
import { useAuth } from "@/context/AuthContext";

const FeaturesSection = () => {
  const { user } = useAuth();
  const getStartedHref = user ? "/dashboard" : "/register";
  return (
    <section id="features" className="py-24 bg-background relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,0,0,0.02),transparent)]" />
      <div className="absolute top-0 left-0 w-full h-full bg-[url('/grid.svg')] opacity-[0.03]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-20">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-6xl font-bold text-primary-text mb-6"
          >
            Backend Brilliance, <br />
            <span className="text-primary">Frontend Focus.</span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg text-secondary-text max-w-2xl mx-auto"
          >
            We handle the complex infrastructure so you can build stunning user experiences.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-[300px]">
          {/* Large Feature 1 */}
          <BentoCard
            title="Visual Schema Design"
            description="Design your database visually. Create relationships, define constraints, and see your architecture come to life."
            icon={Layers3}
            className="md:col-span-2"
            gradient="from-blue-500/10 to-cyan-500/10"
            iconColor="text-blue-500"
          />

          {/* Feature 2 */}
          <BentoCard
            title="Instant APIs"
            description="Auto-generated CRUD endpoints for every model. REST & GraphQL ready."
            icon={Zap}
            className="md:col-span-1"
            gradient="from-yellow-500/10 to-orange-500/10"
            iconColor="text-orange-500"
          />

          {/* Feature 3 */}
          <BentoCard
            title="AI-Powered Docs"
            description="Upload SRS documents and let AI build your data models automatically."
            icon={Search}
            className="md:col-span-1"
            gradient="from-purple-500/10 to-pink-500/10"
            iconColor="text-purple-500"
          />

          {/* Large Feature 4 */}
          <BentoCard
            title="Multi-Database Support"
            description="Switch between PostgreSQL and MongoDB seamlessly. No code rewrites needed."
            icon={Database}
            className="md:col-span-2"
            gradient="from-green-500/10 to-emerald-500/10"
            iconColor="text-green-500"
          />

          {/* Feature 5 */}
          <BentoCard
            title="Enterprise Security"
            description="Built-in Rate Limiting, CORS, and Auth0 integration."
            icon={Lock}
            className="md:col-span-1"
            gradient="from-red-500/10 to-rose-500/10"
            iconColor="text-red-500"
          />

           {/* Feature 6 */}
           <BentoCard
            title="Chat Assistant"
            description="Refine models with natural language prompts."
            icon={MessageSquare}
            className="md:col-span-1"
            gradient="from-indigo-500/10 to-violet-500/10"
            iconColor="text-indigo-500"
          />
           
           {/* Call to Action Card */}
           <Link href={getStartedHref}>
             <motion.div 
               initial={{ opacity: 0, scale: 0.95 }}
               whileInView={{ opacity: 1, scale: 1 }}
               viewport={{ once: true }}
               className="md:col-span-1 bg-primary text-background rounded-3xl p-8 flex flex-col justify-center items-center text-center cursor-pointer hover:opacity-90 transition-opacity group shadow-xl"
             >
               <h3 className="text-2xl font-bold mb-2">Start Building</h3>
               <p className="opacity-90 mb-6">Get your backend running in minutes.</p>
               <div className="bg-background/20 p-3 rounded-full group-hover:scale-110 transition-transform">
                 <ArrowRight className="w-6 h-6" />
               </div>
             </motion.div>
           </Link>

        </div>
      </div>
    </section>
  );
};

const BentoCard = ({ title, description, icon: Icon, className = "", gradient, iconColor }: any) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className={`relative overflow-hidden rounded-3xl bg-surface border border-border p-8 hover:shadow-lg transition-all duration-300 group ${className}`}
    >
      <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
      
      <div className="relative z-10 h-full flex flex-col">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 border border-border bg-background shadow-sm`}>
          <Icon className={`w-6 h-6 ${iconColor}`} />
        </div>
        
        <h3 className="text-xl font-bold text-primary-text mb-3">{title}</h3>
        <p className="text-secondary-text leading-relaxed">{description}</p>
      </div>
    </motion.div>
  );
};

export default FeaturesSection;